import { FC, useCallback, useState } from "react";
import { Table, TableBody, TableRow } from "@mui/material";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu } from "@framework/provider-asu";
import { useApiCall, useDeepCompareEffect } from "@framework/react-hooks";

import { StyledTableCell } from "./styled";
import { TrainInfoTable } from "./train-info";

export interface IInfoResponseTrain {
  Name: string;
  Description: string;
  Position: string;
  EquipmentGroup: string;
}

export interface IInfoResponsePath {
  idTopLevel: number;
  idParent: number | null;
  Name: string;
}

export interface IInfoResponsePoint {
  ControlledParameter: number;
  count: number;
}

export interface ITrainResponse {
  train: IInfoResponseTrain[];
  path: IInfoResponsePath[];
  points: IInfoResponsePoint[];
}

export interface IStationResponse {
  Name: string;
  Description: string;
  count: number;
}

export interface IInfoTrain {
  name?: string;
  description: string;
  position: string;
  rank: string;
  station: string;
  totalPoints: number;
  points: IPoint[];
}

export interface IPoint {
  name: string;
  value: string;
}

export interface IInformationTable {
  showTrain?: boolean;
  isOnlyPage?: boolean;
}

export const InformationTable: FC<IInformationTable> = props => {
  const { showTrain = true, isOnlyPage = false } = props;
  const { selectedItem } = useAsu();

  const [trainInfo, setTrainInfo] = useState<ITrainResponse | null>(null);
  const [stationInfo, setStationInfo] = useState<IStationResponse | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const transformTrainInfo = useCallback(
    (json: ITrainResponse): IInfoTrain => {
      const train: IInfoResponseTrain = json.train[0];
      const name = train.Name;
      const description = train.Description;
      const position = train.Position;
      const rank = train.EquipmentGroup;
      const station = json.path.map(({ Name }) => Name).join(" / ");
      const totalPoints = json.points.reduce((memo, point) => memo + point.count, 0);
      const points = json.points.map(({ ControlledParameter, count }) => ({
        name:
          typeof ControlledParameter === "number"
            ? formatMessage({ id: `pages.journals.popup.train.points.${ControlledParameter}` })
            : "",
        value: `${count}/${totalPoints}`,
      }));

      const info: IInfoTrain = {
        name: undefined,
        description,
        position,
        rank,
        station,
        totalPoints,
        points,
      };

      if (showTrain) {
        info.name = name;
      }

      return info;
    },
    [showTrain, formatMessage],
  );

  const renderStation = useCallback(() => {
    if (!stationInfo) {
      return null;
    }

    return (
      <>
        {Object.keys(stationInfo).map(key => (
          <TableRow key={key}>
            <StyledTableCell>
              {formatMessage({ id: `pages.journals.popup.station.${key.toLowerCase()}` })}
            </StyledTableCell>
            <StyledTableCell>{(stationInfo as Record<string, any>)[key]}</StyledTableCell>
          </TableRow>
        ))}
      </>
    );
  }, [stationInfo, formatMessage]);

  const renderTrain = useCallback(() => {
    if (!trainInfo) {
      return null;
    }

    const info: IInfoTrain = transformTrainInfo(trainInfo);

    return <TrainInfoTable trainInfo={info} showTrain={showTrain} isOnlyPage={isOnlyPage} />;
  }, [trainInfo, transformTrainInfo, isOnlyPage, showTrain]);

  const { fn, isLoading } = useApiCall(
    (api, { trainId, stationId }: { trainId?: number; stationId?: number }) => {
      const url = `/journal/${trainId ? `train/${trainId}/popup` : stationId ? `station/${stationId}/popup` : ""}`;

      return api
        .fetchJson({
          url,
          method: "GET",
        })
        .then(json => {
          if (trainId) {
            setTrainInfo(json);
          } else {
            setStationInfo(json[0]);
          }
        })
        .catch((error: any) => {
          console.error(error);
          enqueueSnackbar(formatMessage({ id: "pages.journals.reports.downloadError" }), { variant: "error" });
        });
    },
    { success: false, error: false },
  );

  useDeepCompareEffect(() => {
    if (!selectedItem) {
      return;
    }

    if ("TrainId" in selectedItem) {
      void fn(undefined, { trainId: selectedItem.TrainId });
    } else if ("Id" in selectedItem) {
      void fn(undefined, { stationId: selectedItem.Id });
    }
  }, [{ selectedItem }]);

  const isStation = selectedItem && "Id" in selectedItem;

  return (
    <ProgressOverlay isLoading={isLoading}>
      <Table sx={{ width: "100%" }}>
        <TableBody>{isStation ? renderStation() : renderTrain()}</TableBody>
      </Table>
    </ProgressOverlay>
  );
};
